import { buildGeo, type SealedBroadcastJson } from "@beacon/client-core";

/** Conservative plaintext cap: the lexicon caps the sealed ciphertext at 8192 bytes;
 *  AEAD overhead is small, so 8000 plaintext bytes is a safe pre-seal guard. */
export const MAX_MESSAGE_BYTES = 8000;

const utf8 = (s: string) => new TextEncoder().encode(s);

/** Minimal client seam (the real BeaconClient satisfies this). */
type PublishClient = {
  epochSnapshot(): Promise<{ keyRef: string; key: Uint8Array }>;
  sealBroadcastWith(snap: { keyRef: string; key: Uint8Array }, pt: Uint8Array): Promise<SealedBroadcastJson>;
  publish(env: { $type: string; payload: Record<string, unknown>; geo: ReturnType<typeof buildGeo> }): Promise<unknown>;
};

export interface MessageInput {
  text: string;
  lang?: string;
  cell: string; // res-8 home cell
}

/**
 * Seal the text under the current epoch snapshot and publish a signed
 * app.aid.message geo-tagged at `cell`. Reach is receiver-side (whoever subscribes
 * to this area/region sees it), mirroring publishReport.
 */
export async function publishMessage(client: PublishClient, input: MessageInput): Promise<void> {
  const text = input.text.trim();
  if (!text) throw new Error("message: empty");
  const encoded = utf8(text);
  if (encoded.length > MAX_MESSAGE_BYTES) throw new Error("message: too long");
  const snap = await client.epochSnapshot();
  const payload: Record<string, unknown> = { text: await client.sealBroadcastWith(snap, encoded) };
  if (input.lang) payload.lang = input.lang;
  await client.publish({ $type: "app.aid.message", payload, geo: buildGeo(input.cell) });
}
